/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core/PageData'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../../util/Functions'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { array, object } from 'yup'
import { COUNTRIES_EN, renderIdentifier } from '../../../../util/Constants'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../../common/component/FilePicker'
import { IAttachment } from '../../../media/Media.model'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../../contact/Contact.model'
import FormValidator from '../../../../util/FormValidator.util'
import { IClientDto, IClientFilter } from '../../../client/Client.model'
import { IFilterDto } from '../../../../common/model/Core.model'
import { useClientGetByIdMutation, useClientSearchMutation } from '../../../client/Client.api'
import { IAssetDto, IAssetFilter } from '../../../asset/Asset.model'
import { useAssetGetByIdMutation, useAssetSearchMutation } from '../../../asset/Asset.api'
import { useTicketGetByIdMutation, useTicketMediaDeleteByIdMutation, useTicketUpdateByIdMutation } from '../Ticket.api'
import { Spinner } from '../../../../common/component/Spinner'
import classNames from 'classnames'
import { TagInputSearchListControls } from '../../../../common/component/TagInputSearchListControls'
import { ClientListItem } from '../../../client/components/ClientListItem'
import { AssetListItem } from '../../../asset/components/AssetListItem'
import { getCriticality, getStatusTypes, IATicket, ImpactLevelOptions, ITicketDto, ITicketUpdateRequestDto, mapCriticalityLabelColor, PriorityLevelOptions } from '../Ticket.model'
import { DateTimeInputPicker } from '../../../../common/component/DateTimeInputPicker'
import { ContactPicker } from '../../../contact/components/ContactPicker'
import { ContactListItem } from '../../../contact/components/ContactListItem'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'
import { parseLocalDate } from '../../../../util/Locale'
import { useContactGetByIdMutation } from '../../../contact/Contact.api'
import { ContractEmbedered } from '../../../contract/components/ContractEmbedered'

interface IUpdateFormikSchema {
  clientId: string
  assetId?: string
  id: string
  statusCode: string
  status: string
  reportedAt: string
  reportedById: string
  impactCode: string
  impact: string
  priorityCode: string
  priority: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  resolution?: string
  closingNotes?: string
  attachments: IAttachment[]
}

export const TicketEditPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const {ticketId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
  ]
  
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      clientId: formValidator.validateStringStrict(),
      assetId: formValidator.validateString(),
      reportedAt: formValidator.validateStringStrict(),
      reportedById: formValidator.validateStringStrict(),
      statusCode: formValidator.validateStringStrict(),
      impactCode: formValidator.validateStringStrict(),
      priorityCode: formValidator.validateStringStrict(),
      title: formValidator.validateStringStrict(),
      description: formValidator.validateTextArea(),
      notes: formValidator.validateTextArea(),
      countryCode: formValidator.validateString(),
      country: formValidator.validateString(),
      address: formValidator.validateString(),
      latitude: formValidator.validateString(),
      longitude: formValidator.validateString(),
      resolution: formValidator.validateString(),
      closingNotes: formValidator.validateString(),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
    clientId: '',
    id: `${ticketId}`,
    reportedAt: '',
    reportedById: '',
    statusCode: '',
    status: '',
    impactCode: '',
    impact: '',
    priorityCode: '',
    priority: '',
    title: '',
    attachments: [],
  })

  const clientFilterKeys = {
    defaultKey: IClientFilter.NAME,
    defaultKeyList: [
      IClientFilter.ID,
      IClientFilter.NAME,
      IClientFilter.SECTOR,
      IClientFilter.ADDRESS,
    ],
  }
  const didFetchClient = useRef(false)
  const [clientFilterSuggestions, setClientFilterSuggestions] = useState<string[]>(clientFilterKeys.defaultKeyList)
  const [clientFilters, setClientFilters] = useState<IFilterDto[]>([])
  const [client, setClient] = useState<IClientDto | undefined>()
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [clientSearchResult, setClientSearchResult] = useState<IClientDto[] | undefined>(undefined)
  const [clientGetById] = useClientGetByIdMutation()
  const [clientSearch] = useClientSearchMutation()

  const assetFilterKeys = {
    defaultKey: IAssetFilter.CLIENT,
    defaultKeyList: [
      IAssetFilter.ID,
      IAssetFilter.FAMILY,
      IAssetFilter.AREA,
      IAssetFilter.BRAND,
      IAssetFilter.MODEL,
      IAssetFilter.SERIAL,
      IAssetFilter.ADDRESS,
    ],
  }
  const didFetchAsset = useRef(false)
  const [assetFilterSuggestions, setAssetFilterSuggestions] = useState<string[]>(assetFilterKeys.defaultKeyList)
  const [assetFilters, setAssetFilters] = useState<IFilterDto[]>([])
  const [asset, setAsset] = useState<IAssetDto | undefined>(undefined)
  const [isAssetLoading, setIsAssetLoading] = useState(false)
  const [assetSearchResult, setAssetSearchResult] = useState<IAssetDto[] | undefined>(undefined)
  const [assetGetById] = useAssetGetByIdMutation()
  const [assetSearch] = useAssetSearchMutation()
  const [contactGetById] = useContactGetByIdMutation()
  const [contact, setContact] = useState<IContactDto | undefined>(undefined) 
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)

  const didFetchTicket = useRef(false)
  const [ticket, setTicket] = useState<ITicketDto | undefined>()
  const [isTicketLoading, setIsTicketLoading] = useState(true)
  const [ticketGetById] = useTicketGetByIdMutation()
  const [ticketUpdateById] = useTicketUpdateByIdMutation()

  const didTicketMediaRequestDelete = useRef(false)
  const [ticketMediaDeleteById] = useTicketMediaDeleteByIdMutation()
  
  const formik = useFormik<IUpdateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)
          const request: ITicketUpdateRequestDto = {
            clientId: values.clientId,
            assetId: values.assetId,
            id: values.id,
            statusCode: values.statusCode,
            status: intl.formatMessage({
              id: `DROPDOWN.TICKET.STATUS.${values.statusCode}`,
            }),
            reportedAt: values.reportedAt,
            reportedBy: values.reportedById,
            impactCode: values.impactCode,
            impact: intl.formatMessage({
              id: `TICKET.IMPACT.${values.impactCode}`,
            }),
            priorityCode: values.priorityCode,
            priority: intl.formatMessage({
              id: `TICKET.PRIORITY.${values.priorityCode}`,
            }),
            criticalityCode: getCriticality(formik.values.impactCode, formik.values.priorityCode),
            criticality: intl.formatMessage({
              id: `TICKET.CRITICALITY.${getCriticality(
                formik.values.impactCode,
                formik.values.priorityCode
              )}.LABEL`,
            }),
            title: values.title,
            description: values.description,
            notes: values.notes,
            countryCode: values.countryCode,
            country: values.countryCode
              ? intl.formatMessage({
                  id: `DROPDOWN.COUNTRY.${values.countryCode}`,
                })
              : undefined,
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            resolution: values.resolution,
            closingNotes: values.closingNotes,
            attachments: values.attachments,
          }

          const response: any = await ticketUpdateById(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/tickets/${ticketId}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchTicketData = async (id) => {
      try {
        if (!didFetchTicket.current) {
          setIsTicketLoading(true)
          const response: any = await ticketGetById({id})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const data: IATicket = response.data
            setTicket(data.ticket)

            formik.setFieldValue('id', data.ticket?.id)
            formik.setFieldValue('reportedAt', data.ticket?.reportedAt)
            formik.setFieldValue('statusCode', data.ticket?.statusCode)
            formik.setFieldValue(
              'status',
              intl.formatMessage({id: `TICKET.STATUS.${data.ticket?.statusCode}`})
            )
            formik.setFieldValue('impactCode', data.ticket?.impactCode)
            formik.setFieldValue(
              'impact',
              intl.formatMessage({
                id: `TICKET.IMPACT.${data.ticket?.impactCode}`,
              })
            )
            formik.setFieldValue('priorityCode', data.ticket?.priorityCode)
            formik.setFieldValue(
              'priority',
              intl.formatMessage({
                id: `TICKET.PRIORITY.${data.ticket?.priorityCode}`,
              })
            )
            formik.setFieldValue('criticalityCode', data.ticket?.criticalityCode)
            formik.setFieldValue(
              'criticality',
              intl.formatMessage({
                id: `TICKET.CRITICALITY.${data.ticket?.criticalityCode}`,
              })
            )
            formik.setFieldValue('title', data.ticket?.title)
            formik.setFieldValue('description', data.ticket?.description)
            formik.setFieldValue('notes', data.ticket?.notes)
            formik.setFieldValue('countryCode', data.ticket?.countryCode)
            formik.setFieldValue('country', data.ticket?.country)
            formik.setFieldValue('address', data.ticket?.address)
            formik.setFieldValue('latitude', data.ticket?.latitude)
            formik.setFieldValue('longitude', data.ticket?.longitude)
            formik.setFieldValue('resolution', data.ticket?.resolution)
            formik.setFieldValue('closingNotes', data.ticket?.closingNotes)
            formik.setFieldValue('attachments', data.media.map((media) => ({
              createdAt: media.createdAt,
              id: media.id,
              fileName: media.fileName,
              temporalPath: media.temporalPath,
              path: media.path,
              size: media.size,
              contentType: media.contentType,
              comments: media.comments,
              status: media.status,
              processing: false,
              uploaded: true,
            })))

            if (data.ticket?.client.id) {
              fetchClient(data.ticket.client.id)
            }
            if (data.ticket?.asset?.id) {
              fetchAsset(data.ticket.asset.id)
            }
            if (data.ticket?.reportedBy?.id) {
              fetchContact(data.ticket.reportedBy.id)
            }
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsTicketLoading(false)
      }
    }
    const fetchClient = async (id) => {
      setIsClientLoading(true)
      const response: any = await clientGetById({id})
      if (response && response.data && !response.error) {
        const clientData: IClientDto = response.data
        setClient(clientData)
        setClientSearchResult(undefined)
        formik.setFieldValue('clientId', clientData.id)
        setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: clientData.id, editable: false}])
      }
      setIsClientLoading(false)
    }
    const fetchAsset = async (id) => {
      setIsAssetLoading(true)
      const response: any = await assetGetById({id})
      if (response && response.data && !response.error) {
        const assetData: IAssetDto = response.data
        setAsset(assetData)
        formik.setFieldValue('assetId', assetData.id)
      }
      setIsAssetLoading(false)
    }
    const fetchContact = async (id) => {
      const response: any = await contactGetById({id})
      if (response && response.data && !response.error) {
        const contactData: IContactDto = response.data
        setContact(contactData)
        formik.setFieldValue('reportedById', contactData.id)
      }
    }
    if (ticketId) {
      fetchTicketData(ticketId)
    }
  }, [ticketId])

  const clientRowOnClickHandler = async (id) => {
    if (clientSearchResult) {
      const client = clientSearchResult.find((client) => client.id === id)
      if (client) {
        setClient(client)
        setClientSearchResult(undefined)
        formik.setFieldValue('clientId', client.id)
        setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
      }
    }
  }

  const clientRemoveOnClickHandler = async () => {
    setClient(undefined)
    setClientSearchResult(undefined)
    setClientFilters([])
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

    setAsset(undefined)
    setAssetSearchResult(undefined)
    setAssetFilters([])
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)

    formik.setFieldValue('clientId', undefined)
    formik.setFieldValue('assetId', undefined)
  }

  const clientSearchOnClickHandler = async () => {
    try {
      if (!didFetchClient.current) {
        setIsClientLoading(true)
        const response: any = await clientSearch({filters: clientFilters})
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setClientSearchResult(response.data.data)
          setClientFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  const clientResetSearchResetOnClickHandler = async () => {
    setClientSearchResult(undefined)
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)
  }

  const assetRowOnClickHandler = async (assetId) => {
    if (assetSearchResult) {
      const asset = assetSearchResult.find((asset) => asset.id === assetId)
      if (asset) {
        setAsset(asset)
        setAssetSearchResult(undefined)

        formik.setFieldValue('assetId', asset.id)
        formik.setFieldValue('countryCode', asset.countryCode)
        formik.setFieldValue('address', asset.address)
      }
    }
  }

  const assetRemoveOnClickHandler = async () => {
    setAsset(undefined)
    setAssetSearchResult(undefined)

    if (client) {
      setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
      setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    }

    formik.setFieldValue('assetId', undefined)
    formik.setFieldValue('countryCode', undefined)
    formik.setFieldValue('address', undefined)
  }

  const assetSearchOnClickHandler = async () => {
    try {
      if (client && !didFetchAsset.current) {
        setIsAssetLoading(true)
        const response: any = await assetSearch({filters: assetFilters})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setAssetSearchResult(response.data.data)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsAssetLoading(false)
    }
  }

  const assetResetSearchResetOnClickHandler = async () => {
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    setAssetSearchResult(undefined)
  }

  const contactSelectOnClickHandler = async (contact) => {
    if (contact) {
      setContact(contact)
      formik.setFieldValue('reportedById', contact.id)
    }
  }

  const contactRemoveOnClickHandler = async () => {
    setContact(undefined)
    formik.setFieldValue('reportedById', undefined)
  }

  const ticketMediaDeleteOnClickHandler = async (ticketId: string, id: string) => {
    try {
      if (id && !didTicketMediaRequestDelete.current) {
        const response: any = await ticketMediaDeleteById({ticketId: ticketId, id: id}).unwrap()
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          formik.setFieldValue(
            'attachments',
            formik.values.attachments.filter((attachment) => attachment.id !== id)
          )
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    }
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.clientId && formik.errors.clientId) ||
          (formik.touched.reportedAt && formik.errors.reportedAt) ||
          (formik.touched.reportedById && formik.errors.reportedById) ||
          (formik.touched.impactCode && formik.errors.impactCode) ||
          (formik.touched.priorityCode && formik.errors.priorityCode) ||
          (formik.touched.title && formik.errors.title)
        )
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {ticket && (
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fs-2 fw-bolder'>{`${ticket.title}`}</span>
                          <span className='fw-bolder text-primary me-1 fs-5'>
                            {renderIdentifier(ticket.sequence)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      {ticket && (
                        <>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary me-2 d-none d-md-block'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <>
                                <i className='bi bi-floppy'></i>
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'UTIL.SAVE'})}
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <i className='bi bi-floppy'></i>
                            )}
                          </button>
                        </>
                      )}

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/tickets/${ticketId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isTicketLoading && !formik.isSubmitting ? (
                <Spinner />
              ) : !ticket ? (
                <div className='row'>
                  <div className='d-flex flex-row-fluid flex-start mt-10'>
                    <span className='text-danger '>
                      <div className='d-flex flex-row justify-content-start align-items-center'>
                        <span className='fs-6 me-1'>
                          <i className='text-danger me-1 bi bi-exclamation-octagon' />
                          {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {tabWithError('kt_tab_pane_1') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                  </ul>
                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      <div className='row mb-5'>
                        {!client && (
                          <>
                            <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                              <span
                                className={classNames('required', {
                                  'text-gray-700': !(
                                    formik?.touched?.clientId && formik?.errors?.clientId
                                  ),
                                  'text-danger':
                                    formik?.touched?.clientId && formik?.errors?.clientId,
                                })}
                              >
                                {intl.formatMessage({id: 'UTIL.CLIENT'})}
                              </span>
                            </label>

                            <div className='col-xxl-9'>
                              <TagInputSearchListControls
                                loading={isClientLoading || isAssetLoading}
                                enabled={!formik.isSubmitting}
                                filterKeys={clientFilterKeys}
                                filterSuggestions={clientFilterSuggestions}
                                filters={clientFilters}
                                createOnClickPath={'/clients/new'}
                                setFilterSuggestions={setClientFilterSuggestions}
                                setFilters={setClientFilters}
                                searchOnClickHandler={clientSearchOnClickHandler}
                                resetSearchResetOnClickHandler={
                                  clientResetSearchResetOnClickHandler
                                }
                              ></TagInputSearchListControls>
                            </div>
                            <label className='col-xxl-3'></label>
                            <div className='col-xxl-9 h-3px'>
                              {clientSearchResult && clientSearchResult?.length > 0 && (
                                <ul
                                  id='report-client-autocomplete-list'
                                  className='suggestions-list'
                                  role='listbox'
                                >
                                  {clientSearchResult.map((item, index) => (
                                    <li
                                      key={index}
                                      onClick={() => clientRowOnClickHandler(item.id)}
                                    >
                                      <ClientListItem data={item}></ClientListItem>
                                      <div className='separator'></div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </>
                        )}

                        {client && (
                          <>
                            <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                              <span className='text-gray-700 required'>
                                {intl.formatMessage({id: 'UTIL.CLIENT'})}
                              </span>
                            </label>

                            <div className='col-xxl-9'>
                              <ClientListItem
                                disabled={formik.isSubmitting}
                                data={client}
                                rowSelected={client.id}
                                removeOnClick={clientRemoveOnClickHandler}
                              ></ClientListItem>
                            </div>
                          </>
                        )}
                      </div>

                      {client && (
                        <>
                          <div className='row mb-5'>
                            {!asset && (
                              <>
                                <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span
                                    className={classNames('', {
                                      'text-gray-700': !(
                                        formik?.touched?.assetId && formik?.errors?.assetId
                                      ),
                                      'text-danger':
                                        formik?.touched?.assetId && formik?.errors?.assetId,
                                    })}
                                  >
                                    {intl.formatMessage({id: 'UTIL.ASSET'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9'>
                                  <TagInputSearchListControls
                                    loading={isAssetLoading || isClientLoading}
                                    enabled={!formik.isSubmitting}
                                    filterKeys={assetFilterKeys}
                                    filterSuggestions={assetFilterSuggestions}
                                    filters={assetFilters}
                                    createOnClickPath={`/assets/new?clientId=${client.id}`}
                                    setFilterSuggestions={setAssetFilterSuggestions}
                                    setFilters={setAssetFilters}
                                    searchOnClickHandler={assetSearchOnClickHandler}
                                    resetSearchResetOnClickHandler={
                                      assetResetSearchResetOnClickHandler
                                    }
                                  ></TagInputSearchListControls>
                                </div>
                                <label className='col-xxl-3'></label>
                                <div className='col-xxl-9 h-3px'>
                                  {assetSearchResult && assetSearchResult.length > 0 && (
                                    <ul
                                      id='autocomplete-list'
                                      className='suggestions-list'
                                      role='listbox'
                                    >
                                      {assetSearchResult.map((asset, index) => (
                                        <li
                                          key={index}
                                          onClick={() => assetRowOnClickHandler(asset.id)}
                                        >
                                          <AssetListItem
                                            data={asset}
                                            rowSelected={asset.id}
                                          ></AssetListItem>
                                          <div className='separator'></div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </>
                            )}

                            {asset && (
                              <>
                                <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span className='text-gray-700'>
                                    {intl.formatMessage({id: 'UTIL.ASSET'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9'>
                                  <AssetListItem
                                    disabled={formik.isSubmitting}
                                    data={asset}
                                    rowSelected={asset.id}
                                    removeOnClick={assetRemoveOnClickHandler}
                                  ></AssetListItem>
                                </div>
                              </>
                            )}
                          </div>

                          {asset && asset.contract && (
                            <ContractEmbedered contract={asset.contract} />
                          )}
                        </>
                      )}

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                              value={
                                ticket.createdAt
                                  ? parseLocalDate(
                                      ticket.createdAt,
                                      intl.formatMessage({id: 'I18N.LOCALE'})
                                    )
                                  : ''
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                              value={
                                ticket.updatedAt
                                  ? parseLocalDate(
                                      ticket.updatedAt,
                                      intl.formatMessage({id: 'I18N.LOCALE'})
                                    )
                                  : ''
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.TITLE'})}
                              required={true}
                              placeholder={intl.formatMessage({id: 'TICKET.TITLE'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`title`}
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.STATUS'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='statusCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('statusCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {getStatusTypes(ticket.statusCode).map((status, index) => (
                                    <option key={index} value={status}>
                                      {intl.formatMessage({
                                        id: `DROPDOWN.TICKET.STATUS.${status}`,
                                      })}
                                    </option>
                                  ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <div className='mt-5'>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.CRITICALITY_LEVEL'})}
                                object={
                                  <>
                                    <span
                                      className={mapCriticalityLabelColor(
                                        getCriticality(
                                          formik.values.impactCode,
                                          formik.values.priorityCode
                                        )
                                      )}
                                    >
                                      {intl.formatMessage({
                                        id: `TICKET.CRITICALITY.${getCriticality(
                                          formik.values.impactCode,
                                          formik.values.priorityCode
                                        )}`,
                                      })}
                                    </span>
                                    <IconLabelContent
                                      title={intl.formatMessage({
                                        id: `TICKET.CRITICALITY.${getCriticality(
                                          formik.values.impactCode,
                                          formik.values.priorityCode
                                        )}.LABEL`,
                                      })}
                                      icon={'bi bi-info-lg'}
                                      active={true}
                                      activeColor={'text-primary'}
                                    />
                                  </>
                                }
                              ></CustomInputRenderer>
                            </div>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.IMPACT_LEVEL'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='impactCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('impactCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                  title={intl.formatMessage({
                                    id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                                  })}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {ImpactLevelOptions &&
                                    ImpactLevelOptions.map((impactLevel, index) => (
                                      <option key={index} value={impactLevel}>
                                        {intl.formatMessage({id: `TICKET.IMPACT.${impactLevel}`})}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.PRIORITY_LEVEL'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='priorityCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('priorityCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                  title={intl.formatMessage({
                                    id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                                  })}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {PriorityLevelOptions &&
                                    PriorityLevelOptions.map((priorityLevel, index) => (
                                      <option key={index} value={priorityLevel}>
                                        {intl.formatMessage({
                                          id: `TICKET.PRIORITY.${priorityLevel}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <DateTimeInputPicker
                              label={intl.formatMessage({id: 'UTIL.REPORTED_DATE_TIME'})}
                              required={true}
                              placeholder={intl.formatMessage({id: 'UTIL.REPORTED_DATE_TIME'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='reportedAt'
                            ></DateTimeInputPicker>

                            {!contact && (
                              <div className='row my-5'>
                                <ContactPicker
                                  label={intl.formatMessage({id: 'UTIL.REPORTED_BY'})}
                                  required={false}
                                  enabled={!formik.isSubmitting}
                                  selectOnClick={contactSelectOnClickHandler}
                                ></ContactPicker>
                              </div>
                            )}

                            {contact && (
                              <div className='row my-5'>
                                <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span className='text-gray-700'>
                                    {intl.formatMessage({id: 'UTIL.REPORTED_BY'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9'>
                                  <ContactListItem
                                    disabled={formik.isSubmitting}
                                    data={contact}
                                    rowSelected={contact.id}
                                    removeOnClick={contactRemoveOnClickHandler}
                                  ></ContactListItem>
                                </div>
                              </div>
                            )}

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='description'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                                  {...formik.getFieldProps('description')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.NOTES'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='notes'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.NOTES'})}
                                  {...formik.getFieldProps('notes')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.DIAGNOSTIC_AND_RESOLUTION'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.RESOLUTION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='resolution'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.RESOLUTION'})}
                                  {...formik.getFieldProps('resolution')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'TICKET.CLOSURE_NOTES'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='closingNotes'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'TICKET.CLOSURE_NOTES'})}
                                  {...formik.getFieldProps('closingNotes')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='countryCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('countryCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {COUNTRIES_EN &&
                                    COUNTRIES_EN.map((country, index) => (
                                      <option key={index} value={country.code}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.COUNTRY.${country.code}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              required={false}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='address'
                              enableInputLength={true}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})} />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <FilePicker
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='attachments'
                              fileDeleteOnClickHandler={(id) =>
                                ticketMediaDeleteOnClickHandler(ticket.id, id)
                              }
                              setIsAttachmentProcessing={setIsAttachmentProcessing}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

